<template>
  <main class="select-representation">
    <h1 class="ods-text--size-echo ods-margin-top-0 ods-margin-bottom-3 ods-text--size-alfa-breakpoint-medium">
      {{ $t('selectRepresentation.header') }}
    </h1>
    <div class="ods-content ods-margin-bottom-11 ods-margin-bottom-19-breakpoint-medium">
      <p
        class="ingress"
        v-html="
          $sanitize(
            $t('selectRepresentation.ingress', {
              paragraph_start: '<p>',
              paragraph_stop: '</p>',
              h2_start: '<h2>',
              h2_stop: '</h2>',
              strong_start: '<strong>',
              strong_stop: '</strong>',
              break: '<br>',
              enhetsregisteret: '<a href=\'http://www.brreg.no/\'>Enhetsregisteret</a>',
              altinn:
                '<a href=\'https://info.altinn.no/hjelp/profil/enkelttjenester-og-roller/hvordan-gi-en-enkelttjeneste-og-rolle-til-andre/\'>Altinn</a>',
            })
          )
        "
      />
    </div>
    <section v-if="privateRepresentationAllowed" class="ods-margin-bottom-8 ods-margin-bottom-11-breakpoint-medium">
      <div class="ods-flex-align-items-center-breakpoint-medium ods-margin-bottom-4-breakpoint-medium">
        <h2
          class="select-representation__heading ods-flex-align-items-center ods-text--size-india ods-margin-top-0 ods-margin-bottom-5 ods-text--size-golf-breakpoint-medium"
        >
          <span class="select-representation__heading-icon ods-margin-right-3">
            <span class="ods-icon ods-icon--align-left ods-icon--size-golf ods-icon--user" aria-hidden="true" />
          </span>
          <span class="select-representation__heading-text ods-text--size-golf ods-text--weight-medium">{{
            $t('selectRepresentation.megselv')
          }}</span>
        </h2>
      </div>
      <ul class="select-representation__list ods-margin-bottom-8">
        <li>
          <button
            type="button"
            class="select-representation__button ods-text--size-juliett ods-text--align-left ods-color-bg-gray-light ods-color-text-blue-dark"
            @click="
              selectRepresentation({
                erPrivatPerson: true,
                navn: visningsnavn,
                identifikator: brukerinfo.brukerId,
              })
            "
          >
            <span
              class="select-representation__button-text ods-text--weight-medium ods-inline-block ods-padding-right-2"
              >{{ visningsnavn }}
            </span>
            <span class="select-representation__button-details ods-text--size-lima ods-inline-block">
              {{ $t('selectRepresentation.fnr') }} {{ brukerinfo.brukerId }}
            </span>
          </button>
        </li>
      </ul>
    </section>
    <section v-if="companyRepresentationAllowed" class="ods-margin-bottom-8 ods-margin-bottom-11-breakpoint-medium">
      <div
        v-if="privateRepresentationAllowed"
        class="ods-flex-justify-content-space-between ods-flex-align-items-center ods-flex-breakpoint-medium ods-flex-direction-row-breakpoint-medium ods-margin-bottom-4-breakpoint-medium"
      >
        <h2
          class="select-representation__heading ods-flex-align-items-center ods-text--size-india ods-margin-top-0 ods-margin-bottom-5 ods-text--size-golf-breakpoint-medium"
        >
          <span class="select-representation__heading-icon ods-margin-right-3">
            <span class="ods-icon ods-icon--align-left ods-icon--size-golf ods-icon--organization" aria-hidden="true" />
          </span>
          <span class="select-representation__heading-text ods-text--size-golf ods-text--weight-medium">{{
            $t('selectRepresentation.org')
          }}</span>
        </h2>
        <form action method="get">
          <div v-if="getOrgsToRepresent.length > 10" class="ods-search-inline">
            <label class="sr-only" for="SelectRepresentationSearch">{{ $t('selectRepresentation.search') }}</label>
            <input
              id="SelectRepresentationSearch"
              v-model.trim="searchString"
              class="ods-search-inline__input"
              :placeholder="$t('selectRepresentation.searchPlaceholder')"
              type="search"
              autocomplete="off"
              aria-label="Søk etter organisasjonsnavn"
            />
          </div>
        </form>
      </div>
      <template v-if="getOrgsToRepresent.length > 0">
        <div>
          <div v-if="getOrgsToRepresentWithRole.length">
            <ul class="select-representation__list ods-margin-bottom-8">
              <li v-for="company in getOrgsToRepresentWithRole" :key="company.index">
                <button
                  type="button"
                  class="select-representation__button ods-text--size-juliett ods-text--align-left ods-color-bg-gray-light ods-color-text-blue-dark"
                  @click="selectRepresentation(company)"
                >
                  <span class="select-representation__button-text ods-inline-block ods-padding-right-2">
                    {{ $minside.formatCompanyName(company.navn) }}
                  </span>
                  <span class="select-representation__button-details ods-text--size-lima ods-inline-block">
                    {{ $t('selectRepresentation.orgnr') }} {{ company.identifikator }}
                  </span>
                </button>
              </li>
            </ul>
          </div>
          <div v-if="getOrgsToRepresentWithoutRole.length">
            <span class="select-representation__heading-icon-small">
              <div class="ods-status-message ods-status-message--warning">
                {{ $t('selectRepresentation.orgWithoutRole') }}
              </div>
            </span>
            <ul class="select-representation__list ods-margin-bottom-8">
              <li v-for="company in getOrgsToRepresentWithoutRole" :key="company.index">
                <button
                  type="button"
                  class="select-representation__button ods-text--size-juliett ods-text--align-left ods-color-bg-gray-light ods-color-text-blue-dark"
                  :disabled="!company.harSkjemarolle"
                  @click="selectRepresentation(company)"
                >
                  <span class="select-representation__button-text ods-inline-block ods-padding-right-2">
                    {{ $minside.formatCompanyName(company.navn) }}
                  </span>
                  <span class="select-representation__button-details ods-text--size-lima ods-inline-block">
                    {{ $t('selectRepresentation.orgnr') }} {{ company.identifikator }}
                  </span>
                </button>
              </li>
            </ul>
          </div>
          <ods-status-message
            v-if="!getOrgsToRepresentWithRole.length && !getOrgsToRepresentWithoutRole.length"
            tittel="Melding om advarsel"
            :melding="$t('selectRepresentation.noResults', { searchString })"
            meldingstype="warning"
          />
        </div>
      </template>
      <ods-status-message
        v-else
        class="mt-size-10"
        tittel="Melding om advarsel"
        :melding="$t('shared.error.forbiddenOrganization')"
        meldingstype="warning"
      />
    </section>
  </main>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import OdsStatusMessage from '@/global-components/OdsStatusMessage.vue';

export default {
  name: 'SelectRepresentation',

  components: {
    OdsStatusMessage,
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (vm.getOrgsToRepresent.length === 0) {
        vm.$router.replace({ name: 'AvfallOgGjenvinningMain' });
      } else {
        vm.setBacklink({
          text: vm.$t('breadcrumb.foreldreside.text'),
          href: vm.$t('breadcrumb.foreldreside.href'),
        });
      }
    });
  },

  data: () => ({
    searchString: '',
    privateRepresentationAllowed: true,
    companyRepresentationAllowed: true,
  }),

  computed: {
    ...mapState(['brukerinfo']),
    ...mapState('representasjoner', ['currentRepresentation']),
    ...mapGetters('representasjoner', ['getOrgsToRepresentFiltered', 'getOrgsToRepresent']),
    visningsnavn() {
      return `${this.brukerinfo.fornavn} ${this.brukerinfo.etternavn}`;
    },
    getOrgsToRepresentWithRole() {
      return this.getOrgsToRepresentFiltered.filter((it) => it.harSkjemarolle);
    },
    getOrgsToRepresentWithoutRole() {
      return this.getOrgsToRepresentFiltered.filter((it) => !it.harSkjemarolle);
    },
  },

  watch: {
    searchString() {
      this.setSearchFilter();
    },
    getOrgsToRepresentFiltered: {
      handler() {
        this.setSearchFilter();
      },
      deep: true,
    },
  },

  created() {
    this.searchString = '';
    this.setRepresentationsSearchWord(this.searchString);
  },

  methods: {
    ...mapActions(['addDefaultErrorToGlobalMessage', 'setBacklink']),
    ...mapActions('representasjoner', [
      'getRepresentations',
      'setRepresentationsSearchWord',
      'setCurrentRepresentation',
    ]),

    selectRepresentation(selectedRepresentation) {
      this.setCurrentRepresentation(selectedRepresentation);
      this.$router.push({
        name: 'AvfallOgGjenvinningMain',
      });
    },

    setSearchFilter() {
      this.setRepresentationsSearchWord(this.searchString);
    },
  },
};
</script>

<style lang="scss">
@use 'sass:map';
@use '~@ods/system/colors' as *;

.select-representation {
  &__heading {
    margin-bottom: 0 !important;
    display: inline-flex;

    &-icon {
      width: 2rem;
      line-height: 34px;

      svg {
        vertical-align: top;
      }
    }
  }

  &__list > li {
    margin-top: 1rem;
  }

  &__button {
    $btn-link-bc: $gray-light;
    $btn-focus-bg: $blue-state;
    $btn-focus-bc: $blue-state;
    $btn-focus-txt: $white;
    $btn-hover-bg: $gray-light;
    $btn-hover-bc: $blue-state;
    $btn-hover-txt: $blue-state;
    $btn-active-bg: $blue-state;
    $btn-active-bc: $blue-state;
    $btn-active-txt: $white;
    $btn-disabled-bg: $white;
    $btn-disabled-bc: $white;
    $btn-disabled-txt: $blue-dark;

    border-radius: 0;
    cursor: pointer;
    display: inline-block;
    margin: 0;
    overflow: hidden;
    position: relative;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
    vertical-align: top;
    border: 2px solid $btn-link-bc;
    padding: 0.625rem 0.9375rem;
    width: 100%;

    &-details {
      padding-top: 1px;
      line-height: 1.6875rem;
    }

    &:enabled:focus {
      background-color: $btn-focus-bg;
      border-color: $btn-focus-bc;
      color: $btn-focus-txt;
      outline: 2px solid $btn-focus-bc;
      outline-offset: 2px;
      text-decoration: underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 0.3em;
    }

    &:enabled:hover {
      background-color: $btn-hover-bg;
      border-color: $btn-hover-bc;
      color: $btn-hover-txt;
      outline: 0;
      text-decoration: underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 0.3em;
    }

    &:enabled:active {
      background-color: $btn-active-bg;
      border-color: $btn-active-bc;
      color: $btn-active-txt;
      outline: 0;
      text-decoration: none;
    }

    &:disabled {
      background-color: $btn-disabled-bg;
      border-color: $btn-disabled-bc;
      color: $btn-disabled-txt;
      cursor: inherit;
      padding-left: 0;
    }
  }

  @mixin -icon-mag($status: enabled) {
    $svg-color: #2a2859;
    @if $status != enabled {
      $svg-color: #b9b9b9;
    }

    $-mag: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' id='magnifying-glass-big' fill='none' data-category='ui' viewBox='0 0 32 32'><path fill='#{$svg-color}' d='m30.307 28.782-7.405-7.405A11.946 11.946 0 0 0 25.6 13.8c0-6.627-5.373-12-12-12s-12 5.373-12 12 5.373 12 12 12a11.94 11.94 0 0 0 7.908-2.99l7.385 7.385 1.414-1.413ZM13.6 23.8c-5.514 0-10-4.486-10-10s4.486-10 10-10 10 4.486 10 10c0 5.515-4.486 10-10 10Z' /></svg>") !default;
  }
}
</style>
